/**
 * 속성 창 공통 부품 — 노드마다 제각각이던 머리·칸·라벨 모양을 한 벌로 맞춘다.
 *
 * 기준은 데이터처리 노드 패널이다(현장 요청 2026-09-17: "DB테이블 속성의 레이블·박스 스타일을 데이터처리와
 * 같게, 다른 노드도 일원화"). 새 패널은 이 부품으로 만들고, 기존 패널도 손볼 때 이것으로 옮긴다.
 */
import React from 'react';
import { formatDateTime } from '../../../../utils/formatters';
import { useTranslation } from 'react-i18next';

export const inputCls = 'w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white disabled:bg-gray-100 disabled:text-gray-500';
export const selectCls = inputCls;
export const smallBtn = 'px-2 py-0.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 disabled:opacity-50 whitespace-nowrap';
export const primaryBtn = 'px-2 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap';

/** 패널 머리 — 'Properties' + 노드 유형 + 오른쪽 버튼들 */
export const PanelHeader: React.FC<{ typeLabel: string; right?: React.ReactNode }> = ({ typeLabel, right }) => {
  const { t } = useTranslation();
  return (
  <div className="mb-3 pb-3 border-b border-gray-200">
    <h3 className="text-lg font-medium text-gray-900">{t('property:ui.properties')}</h3>
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-gray-500">{t('property:ui.nodeType')}</span>
      <input type="text" value={typeLabel} readOnly className="text-xs text-gray-700 bg-gray-100 border-none p-1 rounded w-auto" style={{ minWidth: 80 }} />
      {right && <div className="ml-auto flex items-center gap-1">{right}</div>}
    </div>
  </div>
  );
};

/** 칸 위의 라벨. required 면 빨간 * */
export const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean; htmlFor?: string }> = ({ children, required, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-xs font-semibold text-gray-500 mb-1">
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

/**
 * 테두리 있는 묶음 — 제목 줄 오른쪽에 버튼을 둘 수 있다.
 * 칸이 하나뿐인 묶음은 칸 라벨(FieldLabel)을 따로 두지 않는다 — 제목과 같은 말이 두 줄로 겹친다.
 * 그때 필수 표시는 required 로 제목에 붙이고, 칸에는 aria-label 로 이름을 준다.
 */
export const Section: React.FC<{ title: React.ReactNode; right?: React.ReactNode; children: React.ReactNode; className?: string; required?: boolean }> = ({ title, right, children, className, required }) => (
  <div className={`mb-3 rounded border border-gray-200 p-2 ${className || ''}`}>
    <div className="flex items-center justify-between gap-2 mb-1">
      <span className="text-xs font-semibold text-gray-600">{title}{required && <span className="text-red-500 ml-0.5">*</span>}</span>
      {right && <div className="flex items-center gap-1">{right}</div>}
    </div>
    {children}
  </div>
);

/**
 * 접히는 묶음. disabled 면 열 수 없고 흐리게 보인다(예: LOB 항목이 없는 테이블의 LOB 처리).
 * 열림 상태는 사용자가 바꾸면 그대로 두고, defaultOpen 은 처음에만 쓴다.
 */
export const Collapsible: React.FC<{
  title: React.ReactNode; summary?: React.ReactNode; defaultOpen?: boolean; disabled?: boolean; children: React.ReactNode;
}> = ({ title, summary, defaultOpen, disabled, children }) => {
  const [open, setOpen] = React.useState(!!defaultOpen && !disabled);
  React.useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  return (
    <div className={`mb-3 rounded border border-gray-200 ${disabled ? 'bg-gray-50' : ''}`}>
      <button type="button" disabled={disabled} onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-1.5 px-2 py-1.5 text-left ${disabled ? 'cursor-not-allowed' : 'hover:bg-gray-50'}`}>
        <span className={`text-[0.625rem] transition-transform ${open ? 'rotate-90' : ''} ${disabled ? 'text-gray-300' : 'text-gray-500'}`}>▶</span>
        <span className={`text-xs font-semibold ${disabled ? 'text-gray-400' : 'text-gray-600'}`}>{title}</span>
        {summary && <span className="ml-auto text-[0.6875rem] text-gray-500 truncate">{summary}</span>}
      </button>
      {open && !disabled && <div className="px-2 pb-2">{children}</div>}
    </div>
  );
};

/** 'yyyy-mm-dd HH:MM' — 목록·컬럼을 언제 읽었는지 */
export function formatFetchedAt(iso?: string): string {
  return formatDateTime(iso, { dash: '', seconds: false });
}
